<script setup lang="ts">
import { markRaw, onBeforeMount, onMounted, reactive, ref, toRaw } from 'vue'
import { useValidation } from '@/app/core/composable/validation/index'
import { DTab, DTabs, DBtn, DLoading } from 'front-library'
import { replacePayrollTags } from '@/app/core/services/get-table'
import { saveFile } from '@/app/core/services/files'
import { useNotificationsStore } from '@/store/notifications'
import { validateTranslate } from '@/app/core/composable/general'
import { useI18n } from 'vue-i18n'
import DynamicField from './DynamicField.vue'
import Audit from '../../../components/Audit.vue'
import { useUserStore } from '@/store/user'
import { useFeatureFlagsStore } from '@/store/feature-flags'
import { Field } from '@/app/core/services/forms'

const notifications = useNotificationsStore()
const { t } = useI18n()
const store = useUserStore()
const featureFlags = useFeatureFlagsStore()

const props = defineProps({
  formSchema: { type: Object, required: true, default: () => {} },
  initialModel: { type: Object, required: false, default: () => {} },
  readonly: { type: Boolean, required: false, default: false },
  titleBtnSave: { type: String, required: false, default: '' },
  getData: { type: Boolean, required: false, default: false },
  heightFormModal: { type: Boolean, required: false, default: false },
  hasDisableAudit: { type: Boolean, required: false, default: false }
})

const emit = defineEmits([
  'submit',
  'cancel',
  'update',
  'dataFile',
  'updateField',
  'blockSave'
])

const tabs = markRaw<any[]>(
  props.formSchema.tabs.filter((tab: any) =>
    featureFlags.isVisibleForFlags(tab)
  )
)
const fields: Field[] = []

/* Campos que persisten por su cuenta contra otro API y no viajan en el submit del formulario */
const detachedFieldTypes = new Set(['remoteTable'])

/* Datos componente Loading */
const loading = ref(false)

const validation = useValidation()

const {
  model,
  validationSchema,
  handleValidation,
  addDependencyValidation,
  addValidation,
  setInitialModel,
  validateIf,
  translateErrorMessage
} = validation

const files = reactive<any>([])
const optionsSelect = ref<any>({})

function fileType(extension: string): string {
  switch (extension.toLowerCase()) {
    case 'png':
      return 'image/png'
    case 'jpeg':
    case 'jpg':
      return 'image/jpeg'
    case 'pdf':
      return 'application/pdf'
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    case 'odt':
      return 'application/vnd.oasis.opendocument.text'
    default:
      return 'application/octet-stream' // Tipo de contenido genérico si la extensión no es reconocida
  }
}
const onHandleSubmit = async () => {
  if (handleValidation()) {
    loading.value = true

    for (const file of files) {
      if (file.content !== undefined) {
        let type: any = ''
        type = fileType(file.name.split('.').pop() || '')
        model[file.field] = await saveFile(
          file.content,
          file.name,
          undefined,
          undefined,
          type
        )
      }
    }
    loading.value = false
    const submitModel = unFlattenModel(model)
    fields
      .filter(field => detachedFieldTypes.has(field.type))
      .forEach(field => delete submitModel[field.name])
    emit('submit', submitModel)
  } else {
    const errorFields = Object.keys(validationSchema)
      .filter(field => validationSchema[field].errors?.length)
      .map(field => {
        const label = fields.find(f => f.id === field)?.label

        return t('fields.' + field).startsWith('fields.')
          ? label
          : t('fields.' + field)
      })

    const listInvalidFields =
      errorFields.reduce((result, item) => {
        return result + `<li> - ${item}</li>`
      }, ': <ul>') + '</ul>'
    if (listInvalidFields === ': <ul></ul>') {
      notifications.addNotification({
        title: t('notification.invalidFields'),
        text: t('notification.invalidFieldsText'),
        type: 'error',
        time: 1000000
      })
    } else {
      notifications.addNotification({
        title: t('notification.requiredFields'),
        text: t('notification.requiredFieldsText', {
          fields: listInvalidFields
        }),
        type: 'error',
        time: 10000
      })
    }
  }
}

const handleUpdateField = (name: string, value: any) => {
  handleValidation()
  updateModelDependencies(name, value)
  emit('updateField', { name, value })
}

const handleBlockSave = (blocked: boolean) => {
  emit('blockSave', blocked)
}

const updateModelDependencies = (name: string, value: any) => {
  fields.forEach(field => {
    const rawField = toRaw<Field>(field)
    if (typeof rawField.if === 'string' && rawField.if === name) {
      if (!value) {
        model[rawField.name] =
          rawField.type === 'check'
            ? rawField.default || false
            : rawField.default || ''
      }
    }
  })
}

const setFieldsRules = () => {
  fields.forEach(field => {
    addValidation(field.id, field.rules || [])
  })
}

const setFieldValidationDependencies = () => {
  fields.forEach((field, index) => {
    const rawField = toRaw<Field>(field)
    if (rawField.if) {
      const _if = {
        index,
        field:
          typeof rawField.if === 'string' ? rawField.if : rawField.if.field,
        value: typeof rawField.if === 'string' ? true : rawField.if.value
      }

      addDependencyValidation(rawField.id, _if.field, _if.value)
    }
  })
}

const validateFieldDependencies = () => {
  fields.forEach(field => {
    const rawField = toRaw<Field>(field)

    if (rawField.if) {
      const dependencyField =
        typeof rawField.if === 'string' ? rawField.if : rawField.if.field
      const expectedValue =
        typeof rawField.if === 'string' ? true : rawField.if.value
      const currentValue = model[dependencyField]
      const conditionMet = validateIf(currentValue, expectedValue)

      validationSchema[rawField.id].ignoreField = !conditionMet
    } else {
      validationSchema[rawField.id].ignoreField = false
    }
  })
}

const getDefaultValues = () => {
  const defaultValues: Record<string, any> = {}
  fields.forEach(field => {
    if (field && field?.default) {
      if (field.type === 'multiselect') {
        const defaults = Array.isArray(field.default)
          ? field.default
          : [field.default.toString()]

        defaultValues[field.name] = [
          ...defaults.map(_ => replacePayrollTags(_))
        ]
      } else {
        defaultValues[field.name] = field.default.toString().includes(':')
          ? replacePayrollTags(field.default.toString())
          : field.default
      }
      return
    }

    switch (field.type) {
      case 'check':
        defaultValues[field.name] = false
        break
      case 'multiselect':
      case 'table':
      case 'validation':
        defaultValues[field.name] = []
        break
      case 'number':
        defaultValues[field.name] = undefined
        break
      case 'dynamicGroup':
      case 'remoteTable':
        return
      default:
        defaultValues[field.name] = ''
    }
  })

  return defaultValues
}

const overrideEditingProps = () => {
  fields.forEach(field => {
    if (
      field.editingProps &&
      model[field.name] !== undefined &&
      props.titleBtnSave !== t('form.button.save')
    ) {
      field.props = {
        ...field.props,
        ...field.editingProps
      }
    }
  })
}

const flattenModel = (oldModel: any = {}) => {
  const newModel = Object.keys(oldModel).reduce((acc: any, key: string) => {
    if (
      oldModel[key] == null ||
      ['is_inactive', 'isDeleted', 'is_deleted'].includes(key)
    )
      return acc
    if (typeof oldModel[key] === 'object') {
      if (oldModel[key].length > 0) {
        acc[key] = oldModel[key]
      } else {
        for (const [k, v] of Object.entries(oldModel[key])) {
          if (key === 'custom') {
            acc[k] = v // set custom field to first level
            continue
          }
          acc[`${key}.${k}`] = v
        }
      }
    } else {
      acc[key] = oldModel[key]
    }
    return acc
  }, {})
  return newModel
}

const unFlattenModel = (model: any = {}) => {
  return Object.keys(model).reduce((acc: any, key: string) => {
    if (key.includes('.')) {
      if (!acc[key.split('.')[0]]) acc[key.split('.')[0]] = {}
      const value = acc[key.split('.')[0]][key.split('.')[1]]
      acc[key.split('.')[0]] = {
        ...acc[key.split('.')[0]],
        [key.split('.')[1]]: value ?? model[key]
      }
    } else {
      acc[key] = model[key]
    }
    return acc
  }, {})
}

function flattenForm() {
  tabs.forEach((tab, tabIndex: number) => {
    tab.groups.forEach((group: any, groupIndex: number) => {
      fields.push(
        ...group.fields
          .filter((field: Field) => field.name !== '')
          .map((field: Field, index: number) => {
            field.id = `${field.name}@${tabIndex}${groupIndex}${index}`
            return field
          })
      )
    })
  })
}

onBeforeMount(() => {
  flattenForm()
  setFieldsRules()
  setInitialModel({
    ...getDefaultValues(),
    ...flattenModel(props.initialModel)
  })
  overrideEditingProps()
})

onMounted(() => {
  setFieldValidationDependencies()
  validateFieldDependencies()
})

const setModelValue = (fieldName: string, value: any) => {
  model[fieldName] = value
  handleValidation()
}

const getModelValue = (fieldName: string) => {
  return model[fieldName]
}

defineExpose<{
  getModelValue: (fieldName: string) => any
  setModelValue: (fieldName: string, value: any) => void
  setFieldsRules: () => any
}>({
  getModelValue,
  setModelValue,
  setFieldsRules
})
</script>

<template>
  <DLoading v-show="loading" :message="t('loading.files')" />
  <form class="pt-responsive w-full" @submit.prevent="onHandleSubmit">
    <DTabs :height-form-modal="heightFormModal">
      <template v-slot:default="slotProps">
        <DTab
          v-for="tab in tabs"
          :key="validateTranslate(tab.name)"
          :title="validateTranslate(tab.name)"
          :selected-title="slotProps?.selectedTitle">
          <div
            v-for="(group, index) in tab.groups as any[]"
            :key="index"
            class="form-group grid grid-cols-8 px-6 py-4">
            <div
              v-if="group.if ? !!model[group.if] : true"
              class="col-span-8 sm:col-span-3 lg:col-span-2 pb-2 sm:pb-0">
              <p
                class="text-base font-semibold text-gray-700"
                v-html="validateTranslate(group.name)" />
              <p
                v-if="group.description"
                class="text-sm text-gray-500 mr-2"
                v-html="validateTranslate(group.description)" />
            </div>
            <div
              v-if="group.if ? !!model[group.if] : true"
              class="fields-group col-span-8 sm:col-span-5 lg:col-span-6 grid grid-cols-6 gap-x-6 gap-y-4">
              <template
                v-for="(field, index) in group.fields as any[]"
                :key="index">
                <DynamicField
                  v-if="!validationSchema[field.id]?.ignoreField"
                  class="dynamic-field col-span-8 sm:col-span-6 lg:col-span-3 xl:col-span-2"
                  :class="[
                    field.type === 'check'
                      ? 'flex justify-start items-center'
                      : ''
                  ]"
                  :count="index"
                  :field="field"
                  v-model="model[field.name]"
                  :error="
                    translateErrorMessage(t, validationSchema[field.id]?.errors)
                  "
                  :query="field.query"
                  :depends-on="field.dependsOn"
                  :all-model="model"
                  :readonly="readonly"
                  :titleBtnSave="titleBtnSave"
                  :validation="validation"
                  @update:model-value="handleUpdateField(field.name, $event)"
                  @block-save="handleBlockSave"
                  @update:file="files.push({ field: field.name, ...$event })"
                  @options="optionsSelect = $event" />
              </template>
            </div>
            <hr
              class="col-span-8 mt-14"
              v-if="index != tab.groups.length - 1" />
          </div>
        </DTab>
      </template>
    </DTabs>
    <div class="p-5 bg-white shadow-md h-24 rounded-br-md rounded-bl-md">
      <hr class="pb-5" />
      <div
        class="flex items-center justify-between"
        :class="[
          initialModel.id && store.hasPermission('audit:get')
            ? 'justify-between'
            : 'justify-end'
        ]">
        <Audit
          v-if="
            !hasDisableAudit &&
            initialModel.id &&
            store.hasPermission('audit:get')
          "
          :id="initialModel.id"
          :form="formSchema.entity" />
        <div>
          <DBtn
            type="button"
            class="font-semibold py-1 text-base mr-3"
            color="secondary"
            @click="emit('cancel')">
            {{ getData ? $t('form.button.back') : $t('form.button.cancel') }}
          </DBtn>
          <DBtn
            v-if="!getData && !readonly"
            class="font-semibold py-1 text-base">
            {{ titleBtnSave }}
          </DBtn>
        </div>
      </div>
    </div>
  </form>
</template>

<style scoped>
.form-group:not(:has(.fields-group > .dynamic-field)) {
  display: none;
}
</style>
